import React from 'react';
import classNames from 'classnames';
import { TableColumn, SortDirection } from './Table.types';

interface TableHeaderProps<T = any> {
  /**
   * Array of column configurations.
   */
  columns: TableColumn<T>[];
  
  /**
   * Currently sorted column id.
   */
  sortBy?: string;
  
  /**
   * Current sort direction.
   */
  sortDirection?: SortDirection;
  
  /**
   * Sort change handler.
   */
  onSortChange?: (column: string, direction: SortDirection) => void;
  
  /**
   * Whether the header should be fixed (sticky).
   */
  fixed?: boolean;
}

/**
 * TableHeader component that renders the column headers and handles sorting.
 */
export const TableHeader = <T extends {}>({
  columns,
  sortBy,
  sortDirection = 'asc',
  onSortChange,
  fixed = false,
}: TableHeaderProps<T>) => {
  const handleSortClick = (columnId: string) => {
    if (!onSortChange) return;
    
    const newDirection = sortBy === columnId && sortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(columnId, newDirection);
  };
  
  // Sort direction indicator
  const SortIcon = ({ direction, active }: { direction: SortDirection, active: boolean }) => {
    if (!active) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
    
    if (direction === 'asc') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary-100" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
        </svg>
      );
    }
    
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary-100" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    );
  };
  
  return (
    <thead className={classNames({ 'sticky top-0 z-10': fixed })}>
      <tr className="text-sm font-semibold tracking-wide text-gray-800 dark:text-gray-100 bg-primary-50/85 dark:bg-slate-800/90 border-b border-gray-200 dark:border-white/10">
        {columns.map((column) => {
          const isColumnSorted = sortBy === column.id;
          
          // Apply responsive classes based on priority
          const responsiveClasses = {
            'hidden md:table-cell': column.priority === 'medium',
            'hidden lg:table-cell': column.priority === 'low',
          };
          
          // Apply alignment classes
          const alignmentClasses = {
            'text-left': column.align === 'left' || !column.align,
            'text-center': column.align === 'center',
            'text-right': column.align === 'right',
          };
          
          return (
            <th
              key={column.id}
              className={classNames(
                'px-4 py-3 capitalize',
                responsiveClasses,
                alignmentClasses,
                column.className,
                { 'cursor-pointer hover:bg-primary-50 dark:hover:bg-slate-700/70 hover:text-primary-100 dark:hover:text-white': column.sortable }
              )}
              style={{ width: column.width }}
              onClick={() => column.sortable && handleSortClick(column.id)}
            >
              <div className="flex items-center justify-between gap-1">
                <span>{column.label}</span>
                {column.sortable && (
                  <SortIcon 
                    direction={sortDirection}
                    active={isColumnSorted}
                  />
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
  );
};

export default TableHeader;